import { getMediaType, MediaType } from '@/utils/media';
import { EnumButtonName } from '@calm/evexia-analytics';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { clamp } from '@calm/evexia-web-common';
import { MediaProgramGuide } from '@calm/evexia-web-sdk';
import { FavoriteButton } from '@rhg/components/favorite-button';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useFavoriteGuide } from '@rhg/hooks/use-favorite-guide';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { ProgramContextState } from '@rhg/hooks/use-program-context/types';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    LinearProgress,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SvgIcon,
    Theme,
    Typography,
} from '@rhg/libs/material';
import {
    DocumentGlyph,
    LockOutline,
    VideoFilled,
    Checkmark,
    CheckmarkCircleFilled,
    PlayCircleFilled,
} from '@rhg/libs/material/icons';
import { ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ScheduleCard } from '../horizontal-minimal-card';

const styleContainer = {
    borderBottom: 'solid 1px',
    borderColor: 'divider',
};
const styleProgress = { my: 2, mr: DS_SPACING_UNITS.CONTENT };
const styleTools = { my: 2 };
const styleTool = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
};
const styleToolIcon = {
    color: 'text.sub',
    fontSize: 16,
    mr: 2,
};

const styleVideoIcon = (isCompleted: boolean) => ({
    ...(isCompleted
        ? {
              backgroundColor: (theme: Theme) =>
                  theme.palette.mode === 'light'
                      ? theme.palette.mask.white['80p']
                      : 'info.dark',
          }
        : {
              padding: DS_SPACING_UNITS.BASE,
              color: 'primary.contrastText',
              backgroundColor: (theme: Theme) =>
                  theme.palette.mode === 'light'
                      ? theme.palette.mask.black['60p']
                      : theme.palette.mask.white['80p'],
          }),
    borderRadius: '50%',
    m: 2,
});

const styleVideoListItem = {
    marginBottom: DS_SPACING_UNITS.CONTENT,
    paddingRight: DS_SPACING_UNITS.SECTION,
};

interface GuideListItemProps {
    guide: MediaProgramGuide;
    onClick?: (guide: MediaProgramGuide) => void;
    onJoin?: () => void;
    programId: string;
    packClass: ProgramContextState['packClass'];
    programState: ProgramState;
    programType?: ProgramContextState['programType'];
}
export function GuideListItem({
    guide,
    onClick,
    onJoin,
    programId,
    packClass,
    programState,
    programType,
}: GuideListItemProps) {
    const { formatMessage } = useIntl();
    const { toolsByGuide } = useProvidedProgramContext();
    const { duration } = useHumanReadable();

    const { hasPermission } = useActiveCircle();
    const isAbleToPlayPrograms = hasPermission('clinical_program', 'u');

    const playGuide = useCallback(() => {
        onClick?.(guide);
    }, [guide, onClick]);

    const isVideo = useMemo(
        () => getMediaType(guide) === MediaType.Video,
        [guide]
    );

    const trackButton = useMemo<EnumButtonName>(() => {
        if (
            !isAbleToPlayPrograms ||
            (programType === 'clinical_program' &&
                programState !== ProgramState.InProgress &&
                programState !== ProgramState.Completed)
        ) {
            return 'GuideItemLocked';
        } else {
            return 'GuideItemPlay';
        }
    }, [isAbleToPlayPrograms, programState, programType]);

    const iconType = useMemo(() => {
        if (
            !isAbleToPlayPrograms ||
            (programType === 'clinical_program' &&
                programState !== ProgramState.InProgress &&
                programState !== ProgramState.Completed)
        ) {
            return 'locked';
        } else if (guide?.completed) {
            return 'completed';
        }
        if (isVideo) {
            return 'video';
        } else {
            return 'play';
        }
    }, [
        guide?.completed,
        isAbleToPlayPrograms,
        programState,
        programType,
        isVideo,
    ]);

    const icon = useMemo(() => {
        switch (iconType) {
            case 'locked':
                return <LockOutline />;
            case 'completed':
                if (isVideo) {
                    return <Checkmark size="large" color="info" />;
                }
                return <CheckmarkCircleFilled color="info" />;
            case 'video':
                return <VideoFilled sx={styleVideoIcon(false)} />;
            case 'play':
            default:
                return <PlayCircleFilled />;
        }
    }, [iconType, isVideo]);

    const iconLabel = useMemo(() => {
        switch (iconType) {
            case 'locked':
                return formatMessage(commonMessages['common.locked'], {
                    title: guide.title,
                });
            case 'completed':
                return formatMessage(commonMessages['common.completed'], {
                    title: guide.title,
                });
            case 'play':
            case 'video':
            default:
                return formatMessage(commonMessages['common.play'], {
                    title: guide.title,
                });
        }
    }, [iconType, formatMessage, guide.title]);

    const completionPercentage = useMemo(
        () =>
            clamp(
                Math.round(
                    ((guide?.progressDuration ?? 0) /
                        (guide?.totalDuration ?? 1)) *
                        100
                ),
                0,
                100
            ),
        [guide?.progressDuration, guide?.totalDuration]
    );

    const guideTools = useMemo(
        () => toolsByGuide[guide?.id] ?? [],
        [guide?.id, toolsByGuide]
    );

    const { favoriteGuide, canFavorite } = useFavoriteGuide({
        onJoin,
        programId,
        programType,
        packClass,
        programState,
    });

    if (isVideo) {
        return (
            <ListItem
                sx={styleVideoListItem}
                disablePadding
                secondaryAction={
                    <FavoriteButton
                        guide={guide}
                        canFavorite={canFavorite}
                        favoriteGuide={favoriteGuide}
                        trackButtonFavorite="GuideItemFavorite"
                        trackButtonUnfavorite="GuideItemUnfavorite"
                    />
                }
            >
                <ScheduleCard
                    onPlay={playGuide}
                    guide={guide}
                    bottomIconOverride={
                        <SvgIcon
                            aria-label={iconLabel}
                            sx={styleVideoIcon(iconType === 'completed')}
                        >
                            {icon}
                        </SvgIcon>
                    }
                />
            </ListItem>
        );
    }

    return (
        <ListItem
            sx={styleContainer}
            disablePadding
            secondaryAction={
                <FavoriteButton
                    guide={guide}
                    canFavorite={canFavorite}
                    favoriteGuide={favoriteGuide}
                    trackButtonFavorite="GuideItemFavorite"
                    trackButtonUnfavorite="GuideItemUnfavorite"
                />
            }
        >
            <ListItemButton onClick={playGuide} trackButton={trackButton}>
                <ListItemIcon role="note" aria-label={iconLabel}>
                    {icon}
                </ListItemIcon>
                <ListItemText
                    tabIndex={0}
                    secondary={duration(guide?.totalDuration)}
                >
                    {`${guide.position}. ${guide?.title}`}
                    {guide?.completed && (
                        <Typography variant="srOnly">
                            {formatMessage(commonMessages['common.completed'], {
                                title: guide.title,
                            })}
                        </Typography>
                    )}
                    {guideTools.length > 0 && (
                        <Box sx={styleTools}>
                            {guideTools.map((tool, i) => (
                                <Box
                                    key={i}
                                    sx={styleTool}
                                    aria-label={formatMessage(
                                        commonMessages['common.tool'],
                                        {
                                            name: tool.title,
                                        }
                                    )}
                                >
                                    <DocumentGlyph sx={styleToolIcon} />
                                    <Typography
                                        variant="body2"
                                        color="text.sub"
                                        component="span"
                                    >
                                        {tool.title}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {guide?.progressDuration && guide?.totalDuration ? (
                        <LinearProgress
                            color="info"
                            variant="determinate"
                            sx={styleProgress}
                            aria-label={formatMessage(
                                commonMessages['common.percent_complete'],
                                {
                                    percentage: completionPercentage,
                                }
                            )}
                            value={completionPercentage}
                            withCurvedCorners
                        />
                    ) : null}
                </ListItemText>
            </ListItemButton>
        </ListItem>
    );
}
